// libs
import {
  FormGroup,
  FormControl,
  Button,
  Typography,
  CircularProgress,
  ToggleButtonGroup,
  ToggleButton,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

// assets
import ConsultationCodeSelectField from '../ConsultationCodeSelectField';
import { getDisplayCode } from '../utils';
import StepTemplate from 'FRONTEND/components/Calendar/AppointmentForm/StepTemplate';
import {
  updatePayLater,
  pickDesiredConsultationCode,
} from 'FRONTEND/reducers/bookingSlice';
import { useGetConsultationCodesForUserQuery } from 'FRONTEND/utils/backendApi/consultationCodesApi';
import i18n from 'FRONTEND/utils/i18n';


// This component is almost the same as PaymentToggleStep, only without checkbox handling
// The goal is to allow to toggle and select a payment amount separately.
// E.g: Activate payment (PaymentToggleStep, checkbox only) > Select expert > Select payment amount only (PaymentExtraStep)
export const PaymentExtraStep = ({
  isCompleted,
  isLocked = false,
  onStepClick = () => {},
  shouldReset = false,
  onStepCompleted = () => {},
  ...otherProps
}) => {
  const dispatch = useDispatch();
  const selectedExpertId = useSelector((state) => state.booking.selectedExpertId);
  const desiredConsultationCode = useSelector((state) => state.booking.desiredConsultationCode);

  // Paylater toggle state
  const initialPayLaterState = useSelector((state) => state.booking.isPayLater);
  const [isPayLaterActive, setIsPayLaterActive] = useState(initialPayLaterState);
  const handleTogglePayLaterToggle = (newValue) => {
    if (newValue !== null) {
      setIsPayLaterActive(newValue);
      if (newValue) {
        dispatch(pickDesiredConsultationCode({ code: null })); // We send code = null when pay later is active
      }
    }
  };

  const { isLoading, isError } = useGetConsultationCodesForUserQuery(selectedExpertId, { skip: !selectedExpertId });


  useEffect(() => {
    if (shouldReset) {
      dispatch(updatePayLater({ isPayLater: false }));
      dispatch(pickDesiredConsultationCode({ code: null }));
    }
  }, [shouldReset]);

  const handleSubmit = () => {
    dispatch(updatePayLater({ isPayLater: isPayLaterActive }));
    onStepCompleted();
  };

  return (
    <StepTemplate
      label={i18n._('Payment selection')}
      isLocked={isLocked}
      onEditClick={() => {
        dispatch(pickDesiredConsultationCode({ code: null }));
        onStepClick();
      }}
      isStepCompleted={isCompleted}
      editText={isPayLaterActive ? i18n._('Pay later') : getDisplayCode(desiredConsultationCode)}
      {...otherProps}
    >
      <FormGroup sx={{ marginLeft: '2px', display: 'flex', flexDirection: 'column', gap: 1 }}>
        {isError && <Typography color='error'>{i18n._('An error occurred while processing your request.')}</Typography>}
        {isLoading && <CircularProgress />}
        {!isLoading && !isError && (
          <FormControl component='fieldset' variant='standard'>
            <FormGroup>
              <ToggleButtonGroup
                value={isPayLaterActive}
                exclusive
                onChange={(event, newValue) => handleTogglePayLaterToggle(newValue)}
                sx={{ width: 'fit-content' }}
              >
                <ToggleButton value={false}>
                  <Typography variant='body2'>{i18n._('Bill now')}</Typography>
                </ToggleButton>
                <ToggleButton value>
                  <Typography variant='body2'>{i18n._('Bill later')}</Typography>
                </ToggleButton>
              </ToggleButtonGroup>
            </FormGroup>
          </FormControl>
        )}
        {!isPayLaterActive && (
          <ConsultationCodeSelectField />
        )}
      </FormGroup>
      <Button
        variant='contained'
        color='primary'
        sx={{ mt: 1, px: 8, py: 1 }}
        disabled={isError || (!desiredConsultationCode && !isPayLaterActive)}
        onClick={handleSubmit}
      >
        {i18n._('Validate')}
      </Button>
    </StepTemplate>
  );
};
